// components/UserComponents/BillingHistory.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import paymentService from '../../services/paymentService';

// ---- helpers ---------------------------------------------------------------
const fmtMoney = (amount, currency = 'usd') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(
    (amount || 0) / 100
  );

const fmtDate = (ts) =>
  new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleDateString(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });

const statusChip = (status) => {
  const s = (status || '').toLowerCase();
  const map = {
    paid: { color: 'success', label: 'Paid' },
    open: { color: 'warning', label: 'Open' },
    draft: { color: 'default', label: 'Draft' },
    void: { color: 'default', label: 'Void' },
    uncollectible: { color: 'error', label: 'Uncollectible' },
  };
  const { color, label } = map[s] || { color: 'default', label: s || '—' };
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      variant={color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

const pickDescription = (inv) =>
  inv?.description ||
  inv?.lines?.[0]?.description ||
  inv?.lines?.[0]?.price?.nickname ||
  'Subscription';

// ---- component -------------------------------------------------------------
function BillingHistory({ userId, pageSize = 10 }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevStack, setPrevStack] = useState([]); // store first-id of each visited page for Prev
  const [currency, setCurrency] = useState('usd');

  const fetchPage = async ({ starting_after = null, ending_before = null } = {}) => {
    setLoading(true);
    setErr('');
    try {
      const resp = await paymentService.getInvoices(userId);
      if (!resp?.success) throw new Error(resp?.message || 'Failed to fetch billing history');

      const list = Array.isArray(resp.data) ? resp.data : [];
      // Stripe already returns newest-first; keep order
      setInvoices(list);
      setHasMore(Boolean(resp.has_more));
      setNextCursor(resp.starting_after || (list.length ? list[list.length - 1].id : null));
      setCurrency(list[0]?.currency || 'usd');
    } catch (e) {
      setErr(e?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      setPrevStack([]); // reset when user changes
      fetchPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, pageSize]);

  const onNext = async () => {
    if (!invoices.length) return;
    // keep track so we can go back
    setPrevStack((s) => [...s, invoices[0].id]);
    await fetchPage({ starting_after: invoices[invoices.length - 1].id });
  };

  const onPrev = async () => {
    if (!prevStack.length) return;
    const lastFirstId = prevStack[prevStack.length - 1];
    setPrevStack((s) => s.slice(0, -1));
    await fetchPage({ ending_before: lastFirstId });
  };

  const downloadHref = (inv) => inv?.invoice_pdf || inv?.hosted_invoice_url;

  const handleDownload = (inv) => {
    const href = downloadHref(inv);
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadAll = () => {
    invoices.forEach((inv, i) => {
      const href = downloadHref(inv);
      if (href) setTimeout(() => window.open(href, '_blank', 'noopener,noreferrer'), i * 150);
    });
  };

  const rows = useMemo(
    () =>
      invoices.map((inv) => ({
        id: inv.id,
        date: fmtDate(inv.created),
        description: pickDescription(inv),
        amount: fmtMoney(inv.amount_paid ?? inv.total, inv.currency || currency || 'usd'),
        status: inv.status,
        href: downloadHref(inv),
      })),
    [invoices, currency]
  );

  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Billing History
        </Typography>
      </Stack>

      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : err ? (
        <Alert severity="error">{err}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">No invoices yet.</Alert>
      ) : (
        <>
          <Table sx={{ '& td, & th': { borderBottomColor: 'divider' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 160 }}>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell sx={{ width: 140 }}>Amount</TableCell>
                <TableCell sx={{ width: 120 }}>Status</TableCell>
                <TableCell sx={{ width: 140, textAlign: 'right' }}>Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell>{r.amount}</TableCell>
                  <TableCell>{statusChip(r.status)}</TableCell>
                  <TableCell align="right">
                    {r.href ? (
                      <Tooltip title="Download invoice PDF">
                        <Button
                          onClick={() => handleDownload(invoices.find((i) => i.id === r.id))}
                          size="small"
                        >
                          Download
                        </Button>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Paper>
  );
}

export default BillingHistory;
